import { useEffect, useRef, useState } from 'react';

const AGE_VERIFICATION_SESSION_KEY = 'age_verification_confirmed';

const hasConfirmedAgeInSession = () => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(AGE_VERIFICATION_SESSION_KEY) === 'true';
};

const useAgeVerificationGate = () => {
  const [openAgeVerification, setOpenAgeVerification] = useState(false);
  const [openAgeRejected, setOpenAgeRejected] = useState(false);
  const isAgeVerifiedRef = useRef(hasConfirmedAgeInSession());
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!openAgeRejected) return;

    const timer = window.setTimeout(() => {
      setOpenAgeRejected(false);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [openAgeRejected]);

  const runWithAgeVerification = (isAlcoholic: boolean | undefined, action: () => void) => {
    const isVerified = isAgeVerifiedRef.current || hasConfirmedAgeInSession();
    isAgeVerifiedRef.current = isVerified;

    if (isAlcoholic && !isVerified) {
      pendingActionRef.current = action;
      setOpenAgeVerification(true);
      return false;
    }

    action();
    return true;
  };

  const handleConfirmAgeVerification = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    isAgeVerifiedRef.current = true;
    sessionStorage.setItem(AGE_VERIFICATION_SESSION_KEY, 'true');
    setOpenAgeVerification(false);
    action?.();
  };

  const handleCancelAgeVerification = () => {
    pendingActionRef.current = null;
    setOpenAgeVerification(false);
    setOpenAgeRejected(true);
  };

  const handleCloseAgeVerification = () => {
    pendingActionRef.current = null;
    setOpenAgeVerification(false);
  };

  const handleCloseAgeRejected = () => {
    setOpenAgeRejected(false);
  };

  return {
    openAgeVerification,
    openAgeRejected,
    runWithAgeVerification,
    handleConfirmAgeVerification,
    handleCancelAgeVerification,
    handleCloseAgeVerification,
    handleCloseAgeRejected,
  };
};

export default useAgeVerificationGate;
